import React, { useState, useEffect } from 'react';
import { VisibilityPreference } from '../data/auth';
import { auth } from '../firebase/firebase-config';
import {
  GoogleAuthProvider,
  User,
  getRedirectResult,
  signInWithPopup,
  signInWithRedirect,
} from 'firebase/auth';
import {
  getGoogleAuthErrorMessage,
  shouldFallbackToGoogleRedirect,
} from '../utils/google-auth-errors';
import { getApiUrl } from '../utils/api-url';
import './Register.css';

interface ConsultantRegistrationProps {
  onSuccess: () => void;
  onBack: () => void;
  referrerId?: string | null;
}

const ConsultantRegistration: React.FC<ConsultantRegistrationProps> = ({
  onSuccess,
  onBack,
  referrerId,
}) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleUser, setGoogleUser] = useState<{
    email: string;
    name: string;
    googleId: string;
    photoURL?: string;
  } | null>(null);

  const nameParts = googleUser?.name?.split(' ') || [];
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  const [formData, setFormData] = useState({
    // Account credentials
    email: googleUser?.email || '',
    phone: '',
    password: googleUser ? '' : '', // Password not required for Google users
    confirmPassword: googleUser ? '' : '',

    // Profile
    visibilityPreference: 'public' as VisibilityPreference,
    title: '',
    firstName: firstName,
    lastName: lastName,
    qualifications: '',
    certifications: '',
    specialties: '',
    yearsOfExperience: 0,
    linkedIn: '',
    website: '',
    bio: '',
    expertise: '',
    languages: '',
    country: 'South Africa',
    city: '',
    region: '',

    // Privacy
    showFullName: true,
    showContactInfo: false,
    showLocation: true,
  });

  // Update form data when Google user changes
  useEffect(() => {
    if (googleUser) {
      const nameParts = googleUser.name?.split(' ') || [];
      setFormData((prev) => ({
        ...prev,
        email: googleUser.email || prev.email,
        firstName: nameParts[0] || prev.firstName,
        lastName: nameParts.slice(1).join(' ') || prev.lastName,
      }));
    }
  }, [googleUser]);

  const createGoogleProvider = () => {
    const provider = new GoogleAuthProvider();
    provider.addScope('email');
    provider.addScope('profile');
    return provider;
  };

  const applyGoogleUserToForm = (user: User) => {
    const googleUserData = {
      email: user.email || '',
      name: user.displayName || '',
      googleId: user.uid,
      photoURL: user.photoURL || undefined,
    };

    setGoogleUser(googleUserData);

    const parsedNameParts = googleUserData.name.split(' ');
    setFormData((prev) => ({
      ...prev,
      email: googleUserData.email,
      firstName: parsedNameParts[0] || '',
      lastName: parsedNameParts.slice(1).join(' ') || '',
      password: '',
      confirmPassword: '',
    }));

    console.log('Google user data stored and form pre-filled');
  };

  useEffect(() => {
    const handleRedirectSignIn = async () => {
      if (!auth) {
        return;
      }

      try {
        setGoogleLoading(true);
        const redirectResult = await getRedirectResult(auth);

        if (redirectResult?.user) {
          applyGoogleUserToForm(redirectResult.user);
        } else {
          console.warn('Google redirect returned no user during registration');
          setError('Google sign-in did not complete. Please try again.');
        }
      } catch (err) {
        console.error('❌ Redirect sign-in error:', err);
        setError(getGoogleAuthErrorMessage(err));
      } finally {
        setGoogleLoading(false);
      }
    };

    handleRedirectSignIn();
  }, []);

  const handleGoogleSignIn = async () => {
    console.log('🔵 Google Sign-In button clicked for consultant registration');
    console.log('📍 Current URL:', window.location.href);
    console.log('🔧 Firebase Auth Status:', auth ? '✅ Initialized' : '❌ Not initialized');

    setError('');
    setGoogleLoading(true);

    try {
      if (!auth) {
        console.error('❌ Firebase auth not initialized');
        setError('Authentication not initialized. Please refresh the page.');
        setGoogleLoading(false);
        return;
      }

      console.log('✅ Starting Google Sign-In popup for consultant registration...');
      const provider = createGoogleProvider();

      const result = await signInWithPopup(auth, provider);
      if (!result || !result.user) {
        throw new Error('Google sign-in returned no user information.');
      }
      const user = result.user;
      console.log('✅ Google Sign-In successful!');
      console.log('👤 User:', user.email);
      console.log('🆔 UID:', user.uid);

      applyGoogleUserToForm(user);
    } catch (err: any) {
      console.error('❌ Google sign-in error:', err);
      console.error('🔴 Error code:', err.code);
      console.error('🔴 Error message:', err.message);
      console.error('🔴 Full error:', JSON.stringify(err, null, 2));

      if (auth && shouldFallbackToGoogleRedirect(err)) {
        try {
          const provider = createGoogleProvider();
          setError('Popup interrupted. Switching to secure redirect sign-in...');
          sessionStorage.setItem('googleAuthFlow', 'consultant-register');
          await signInWithRedirect(auth, provider);
          return;
        } catch (redirectError) {
          console.error('❌ Redirect sign-in start error:', redirectError);
          setError(getGoogleAuthErrorMessage(redirectError));
          return;
        }
      }

      setError(getGoogleAuthErrorMessage(err));
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData({ ...formData, [name]: checked });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation (skip password validation for Google users)
    if (!googleUser) {
      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }

      if (formData.password.length < 6) {
        setError('Password must be at least 6 characters');
        return;
      }
    }

    setLoading(true);

    try {
      // Use different endpoint for Google users
      const endpoint = googleUser ? '/auth/google-register' : '/auth/register';

      const response = await fetch(getApiUrl(endpoint), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          role: 'professional',
          email: formData.email,
          phone: formData.phone,
          password: googleUser ? undefined : formData.password,
          googleId: googleUser?.googleId,
          googleName: googleUser?.name,
          photoURL: googleUser?.photoURL,
          referrerId: referrerId || undefined,
          profile: {
            type: 'professional',
            visibilityPreference: formData.visibilityPreference,
            title: formData.title,
            firstName: formData.firstName,
            lastName: formData.lastName,
            qualifications: formData.qualifications.split(',').map((q) => q.trim()),
            certifications: formData.certifications.split(',').map((c) => c.trim()),
            specialties: formData.specialties.split(',').map((s) => s.trim()),
            yearsOfExperience: parseInt(formData.yearsOfExperience.toString()),
            linkedIn: formData.linkedIn,
            website: formData.website,
            bio: formData.bio,
            expertise: formData.expertise.split(',').map((e) => e.trim()),
            languages: formData.languages.split(',').map((l) => l.trim()),
            showFullName: formData.showFullName,
            showContactInfo: formData.showContactInfo,
            showLocation: formData.showLocation,
            country: formData.country,
            city: formData.city,
            region: formData.region,
          },
        }),
      });

      const data = await response.json();

      if (data.ok) {
        alert(
          googleUser
            ? 'Registration successful! Your account is pending approval.'
            : 'Registration successful! Please check your email to verify your account.'
        );
        onSuccess();
      } else {
        setError(data.message || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="register-page">
      <div className="register-container large">
        <div className="register-header">
          <button onClick={onBack} className="back-button">
            ← Back
          </button>
          <h1>Consultant Registration</h1>
          <p>Step {step} of 3</p>
        </div>

        <div className="progress-bar">
          <div className={`progress-step ${step >= 1 ? 'active' : ''}`}>Account</div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''}`}>Profile</div>
          <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>Privacy</div>
        </div>

        <form onSubmit={handleSubmit} className="registration-form">
          {step === 1 && (
            <div className="form-step">
              <h2>Account Information</h2>

              {!googleUser && (
                <>
                  <button
                    type="button"
                    onClick={handleGoogleSignIn}
                    className="google-register-button"
                    disabled={googleLoading || loading}
                  >
                    <svg className="google-icon" viewBox="0 0 24 24" width="18" height="18">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    {googleLoading ? 'Signing in...' : 'Sign up with Google'}
                  </button>

                  <div className="register-divider">
                    <span>OR</span>
                  </div>
                </>
              )}

              {googleUser && (
                <div className="google-user-badge">
                  <img src={googleUser.photoURL || '/default-avatar.png'} alt="Profile" />
                  <div>
                    <strong>Signing up with Google</strong>
                    <p>{googleUser.email}</p>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="email">Email Address *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={!!googleUser}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+27821234567"
                  required
                />
              </div>

              {!googleUser && (
                <>
                  <div className="form-group">
                    <label htmlFor="password">Password *</label>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="confirmPassword">Confirm Password *</label>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </>
              )}

              {/* Verification Notice */}
              <div className="verification-notice">
                <h3>⚠️ Verification Process</h3>
                <p>
                  After registration, your consultant profile will be reviewed and verified by our
                  team. This process includes:
                </p>
                <ul>
                  <li>Verification of qualifications and certifications</li>
                  <li>Professional background check</li>
                  <li>Criminal record clearance check (required)</li>
                </ul>
                <p>
                  <strong>Criminal Record Check:</strong> You will need to obtain a Police Clearance
                  Certificate from SAPS or an accredited vendor after registration. Approved vendors
                  include:
                </p>
                <ul>
                  <li>SAPS Criminal Record Centre (official)</li>
                  <li>Managed Integrity Evaluation (MIE)</li>
                  <li>Consult in Africa</li>
                  <li>Afiswitch</li>
                </ul>
                <p className="notice-footer">
                  Once all verification documents are submitted and approved, your profile will be
                  activated and visible to potential clients.
                </p>
              </div>

              <button type="button" onClick={() => setStep(2)} className="next-button">
                Next →
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="form-step">
              <h2>Professional Profile</h2>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="title">Title *</label>
                  <select
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select</option>
                    <option value="Dr">Dr</option>
                    <option value="Prof">Prof</option>
                    <option value="Assoc Prof">Assoc Prof</option>
                    <option value="Mr">Mr</option>
                    <option value="Ms">Ms</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Mx">Mx</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="qualifications">Qualifications (comma separated) *</label>
                <input
                  id="qualifications"
                  name="qualifications"
                  type="text"
                  value={formData.qualifications}
                  onChange={handleChange}
                  placeholder="MBChB, PhD Clinical Research"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="certifications">Certifications (comma separated)</label>
                <input
                  id="certifications"
                  name="certifications"
                  type="text"
                  value={formData.certifications}
                  onChange={handleChange}
                  placeholder="SA-GCP, ACRP, SAHPRA Approved"
                />
              </div>

              <div className="form-group">
                <label htmlFor="specialties">Specialties (comma separated) *</label>
                <input
                  id="specialties"
                  name="specialties"
                  type="text"
                  value={formData.specialties}
                  onChange={handleChange}
                  placeholder="Oncology, Cardiology, Phase II/III Trials"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="yearsOfExperience">Years of Experience *</label>
                <input
                  id="yearsOfExperience"
                  name="yearsOfExperience"
                  type="number"
                  value={formData.yearsOfExperience}
                  onChange={handleChange}
                  min="0"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="expertise">Areas of Expertise (comma separated) *</label>
                <input
                  id="expertise"
                  name="expertise"
                  type="text"
                  value={formData.expertise}
                  onChange={handleChange}
                  placeholder="Protocol Development, Site Management, SAHPRA Submissions"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="languages">Languages (comma separated) *</label>
                <input
                  id="languages"
                  name="languages"
                  type="text"
                  value={formData.languages}
                  onChange={handleChange}
                  placeholder="English, Afrikaans, Zulu"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="bio">Professional Bio</label>
                <textarea
                  id="bio"
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Brief description of your experience and expertise..."
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="country">Country *</label>
                  <input
                    id="country"
                    name="country"
                    type="text"
                    value={formData.country}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="city">City</label>
                  <input
                    id="city"
                    name="city"
                    type="text"
                    value={formData.city}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="region">Region/Province</label>
                  <input
                    id="region"
                    name="region"
                    type="text"
                    value={formData.region}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="linkedIn">LinkedIn Profile</label>
                <input
                  id="linkedIn"
                  name="linkedIn"
                  type="url"
                  value={formData.linkedIn}
                  onChange={handleChange}
                  placeholder="https://linkedin.com/in/yourprofile"
                />
              </div>

              <div className="form-group">
                <label htmlFor="website">Personal Website</label>
                <input
                  id="website"
                  name="website"
                  type="url"
                  value={formData.website}
                  onChange={handleChange}
                  placeholder="https://yourwebsite.com"
                />
              </div>

              <div className="button-row">
                <button type="button" onClick={() => setStep(1)} className="back-button-form">
                  ← Previous
                </button>
                <button type="button" onClick={() => setStep(3)} className="next-button">
                  Next →
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="form-step">
              <h2>Privacy Settings</h2>

              <div className="form-group">
                <label htmlFor="visibilityPreference">Profile Visibility *</label>
                <select
                  id="visibilityPreference"
                  name="visibilityPreference"
                  value={formData.visibilityPreference}
                  onChange={handleChange}
                  required
                >
                  <option value="public">Public - Show my full profile</option>
                  <option value="anonymous">
                    Anonymous - Show only qualifications and expertise
                  </option>
                </select>
                <small>Anonymous profiles reveal identity only after mutual agreement</small>
              </div>

              <div className="privacy-options">
                <h3>What information do you want to show publicly?</h3>

                <div className="checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="showFullName"
                      checked={formData.showFullName}
                      onChange={handleChange}
                    />
                    Show full name (otherwise shows initials)
                  </label>
                </div>

                <div className="checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="showContactInfo"
                      checked={formData.showContactInfo}
                      onChange={handleChange}
                    />
                    Show contact information (email, phone, LinkedIn)
                  </label>
                </div>

                <div className="checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      name="showLocation"
                      checked={formData.showLocation}
                      onChange={handleChange}
                    />
                    Show location (city and region)
                  </label>
                </div>
              </div>

              <div className="info-box">
                <strong>Note:</strong> Regardless of your privacy settings:
                <ul>
                  <li>Your qualifications and expertise are always visible</li>
                  <li>Your full identity is revealed after mutual project acceptance</li>
                  <li>Verified badge is always shown on your profile</li>
                </ul>
              </div>

              {error && <div className="error-message">{error}</div>}

              <div className="button-row">
                <button type="button" onClick={() => setStep(2)} className="back-button-form">
                  ← Previous
                </button>
                <button type="submit" className="submit-button" disabled={loading}>
                  {loading ? 'Creating Account...' : 'Complete Registration'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default ConsultantRegistration;
